import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Cache of previously-rendered <media-gallery> nodes, keyed by image-set signature.
 * Reusing the detached node preserves loaded <img> state, eliminating the flash
 * when a customer returns to a previously-viewed image set. Keying by signature
 * (not variant id) means variants that share the same images — e.g. different
 * sizes of the same color — share a cache slot.
 * @type {Map<string, HTMLElement>}
 */
const galleryCache = new Map();

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Same root-cause fix as `#resetToFirstSlide`, but for the initial page
    // load (not just variant swaps): iOS Safari auto-snaps the scroller to a
    // random slide when `overflow: hidden -> scroll` triggers as the slideshow
    // is added to the viewport. We pin scroll-snap off + scrollLeft = 0 until
    // the slideshow has fully settled, then restore so user-initiated swipes
    // behave normally.
    this.#resetToFirstSlide(this);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const cacheEnabled = this.dataset.cacheEnabled === 'true';

    if (cacheEnabled) {
      const currentSignature = this.dataset.gallerySignature;
      const newSignature = newMediaGallery.getAttribute('data-gallery-signature');

      // Same image set (e.g. same color, different size) — keep the live DOM
      // so nothing reloads. Update the variant id so subsequent comparisons
      // and cache writes reflect the now-selected variant.
      if (currentSignature && newSignature && currentSignature === newSignature) {
        const newVariantId = newMediaGallery.getAttribute('data-variant-id');
        if (newVariantId) this.dataset.variantId = newVariantId;
        return;
      }

      if (currentSignature) {
        galleryCache.set(currentSignature, this);
      }

      if (newSignature && galleryCache.has(newSignature)) {
        const cached = galleryCache.get(newSignature);
        if (cached && cached !== this) {
          this.#resetToFirstSlide(cached);
          this.#preserveHeightDuringSwap(cached);
          this.replaceWith(cached);
          this.#resetToFirstSlide(cached);
          return;
        }
      }
    }

    // Wait for the new first image to be decoded before swapping. On iOS this
    // matters because `<img>` `onload` can fire before WebKit has actually
    // produced a paintable bitmap; `decode()` resolves only when the image is
    // truly ready, eliminating an iOS-only "flash of wrong slide" window.
    await this.#preloadFirstImage(newMediaGallery);

    this.#resetToFirstSlide(newMediaGallery);
    this.#preserveHeightDuringSwap(newMediaGallery);
    this.replaceWith(newMediaGallery);
    this.#resetToFirstSlide(newMediaGallery);
  };

  /**
   * Preloads the first <img> of an incoming media-gallery (the slide-0 image)
   * so the swap can happen with the image already decoded for paint.
   *
   * Uses `image.decode()` rather than `onload` because on iOS Safari `onload`
   * fires when bytes are downloaded but BEFORE WebKit has actually decoded the
   * bitmap. Decoding can run on a separate thread and the IntersectionObserver
   * in the slideshow can fire during that gap, marking the wrong slide active.
   * `decode()` resolves only after the image is fully ready to paint.
   *
   * Races against a 1.5s timeout — never holds the swap indefinitely on a
   * slow network or unsupported decode() implementation.
   *
   * @param {HTMLElement} gallery
   * @returns {Promise<void>}
   */
  #preloadFirstImage(gallery) {
    const firstImg = /** @type {HTMLImageElement|null} */ (gallery.querySelector('img'));
    if (!firstImg) return Promise.resolve();

    const src = firstImg.getAttribute('src');
    const srcset = firstImg.getAttribute('srcset');
    const sizes = firstImg.getAttribute('sizes');

    if (!src && !srcset) return Promise.resolve();

    const preloader = new Image();
    // Use srcset+sizes if present so the browser picks the same resource it
    // will need on the actual <img>, sharing the HTTP cache entry.
    if (srcset) preloader.srcset = srcset;
    if (sizes) preloader.sizes = sizes;
    if (src) preloader.src = src;

    const decodePromise = preloader.decode().catch(() => {});
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 1500));

    return Promise.race([decodePromise, timeoutPromise]);
  }

  /**
   * After a variant change, always show the first image of the (newly selected
   * or cached) gallery — otherwise the user can end up on whatever slide index
   * the previous gallery was on, which feels random.
   *
   * On iOS Safari we cannot rely solely on slideshow.select(0): the slideshow's
   * `connectedCallback` is async (it awaits in-progress view transitions) and
   * `#scroll` is not initialized synchronously, so an early select() is a
   * no-op. Meanwhile the scroller can momentarily display whatever scrollLeft
   * the browser ended up with, which on iOS lands somewhere unpredictable.
   *
   * Strategy:
   *   1. Mark the new slideshow with initial-slide="0" BEFORE insertion so
   *      that when its connectedCallback runs, it starts on slide 0.
   *   2. Reset aria-hidden across slides so only slide 0 is "active" in markup.
   *   3. After insertion, repeatedly force scroller.scrollLeft = 0 across a
   *      few animation frames until #scroll is initialized, then call select(0)
   *      as a final lock. This is bulletproof on iOS regardless of timing.
   *
   * @param {HTMLElement} gallery - The media-gallery now in (or about to enter) the DOM.
   */
  #resetToFirstSlide(gallery) {
    const slideshow =
      /** @type {any} */ (gallery).slideshow ??
      gallery.querySelector('slideshow-component');
    if (!slideshow) return;

    slideshow.setAttribute('initial-slide', '0');
    const slides = slideshow.querySelectorAll('slideshow-slide');
    slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    });

    const scroller = /** @type {HTMLElement|null} */ (
      slideshow.querySelector('[ref="scroller"]')
    );
    if (!scroller) return;

    // Root-cause fix: iOS Safari auto-snaps when the scroller transitions from
    // `overflow: hidden` (initial state, before `in-viewport` attr is added)
    // to `overflow: scroll`. With `scroll-snap-type: x mandatory` engaged, iOS
    // picks an arbitrary snap point — for PopFerry's narrow-with-peek slides
    // that ends up being slide 1 (scrollLeft = slide_width = 345).
    //
    // While the slideshow transitions into the viewport AND while its first
    // image is still decoding (the latter matters on reload, where the layout
    // shifts when the image finally renders and can re-trigger an iOS auto-
    // snap if snap is already engaged), keep scroll-snap OFF and scroll-
    // behavior auto inline. Re-engage snap only after the first image is
    // decoded (or after 1.5s safety timeout) so iOS has nothing to snap to
    // during the critical window.
    const prevSnapType = scroller.style.scrollSnapType;
    const prevScrollBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = 0;
    scroller.scrollTop = 0;

    const firstImg = /** @type {HTMLImageElement|null} */ (
      gallery.querySelector('img')
    );
    const imgReady =
      firstImg && typeof firstImg.decode === 'function'
        ? firstImg.decode().catch(() => {})
        : Promise.resolve();

    Promise.race([
      // Wait for first image decode + one frame of layout settle, then release.
      imgReady.then(
        () => new Promise((r) => requestAnimationFrame(() => r(undefined)))
      ),
      // Safety net: never hold the lock indefinitely on a broken image.
      new Promise((r) => setTimeout(r, 1500)),
    ]).then(() => {
      // Re-assert scrollLeft = 0 in case anything moved during the window,
      // then restore the inline styles so user-initiated scrolls behave normally.
      scroller.scrollLeft = 0;
      scroller.style.scrollSnapType = prevSnapType;
      scroller.style.scrollBehavior = prevScrollBehavior;
    });
  }

  /**
   * Prevents layout shift during a variant-driven gallery swap: snapshots the
   * current gallery's rendered height and applies it as `min-height` on the
   * incoming gallery, then releases it once the first image of the new gallery
   * has loaded (so the container can re-flow to the new content's natural size).
   *
   * Without this, the new gallery briefly collapses to near-zero height between
   * being inserted into the DOM and its <img> elements finishing their network
   * fetch — causing visible jank on the product page, especially on mobile.
   *
   * @param {HTMLElement} incoming - The media-gallery element about to be inserted.
   */
  #preserveHeightDuringSwap(incoming) {
    const currentHeight = this.getBoundingClientRect().height;
    if (currentHeight <= 0) return;
    incoming.style.minHeight = `${currentHeight}px`;

    // Collect all <img> elements (mobile only renders one slide visually, but
    // images may pre-load; we only need the first to settle to release the lock).
    const images = /** @type {HTMLImageElement[]} */ (
      Array.from(incoming.querySelectorAll('img'))
    );

    const release = () => {
      // Two rafs: first lets the new image draw, second lets layout settle
      // before we drop the min-height so we never re-trigger a visible reflow.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          incoming.style.minHeight = '';
        });
      });
    };

    if (images.length === 0) {
      release();
      return;
    }

    // Use the first image as the height anchor. If it's already complete (cached),
    // release on the next frame. Otherwise wait for its load/error event.
    const first = images[0];
    if (first.complete && first.naturalWidth > 0) {
      release();
      return;
    }

    let released = false;
    const onceRelease = () => {
      if (released) return;
      released = true;
      release();
    };

    first.addEventListener('load', onceRelease, { once: true });
    first.addEventListener('error', onceRelease, { once: true });

    // Safety net: never hold the min-height for more than 2 seconds even if
    // the load event somehow never fires.
    setTimeout(onceRelease, 2000);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
